/**
 * Маршруты админки (docs/frontend/deltas.md): `/:section`, `/:section/:id?tab=…`;
 * фильтры, сортировка, страница и поиск реестра — в query-параметрах URL.
 */
export const DEFAULT_SECTION = 'dashboard';

export const ROUTES = {
  login: '/login',
  root: '/',
  section: (section: string) => `/${encodeURIComponent(section)}`,
  record: (section: string, id: string) =>
    `/${encodeURIComponent(section)}/${encodeURIComponent(id)}`,
} as const;

/** Переход в реестр раздела с фильтрами (`SectionLinkOut` бэкенда: `{section, filters}`). */
export interface SectionLink {
  section: string;
  filters: Readonly<Record<string, string>>;
}

/**
 * `/:section?filter[key]=value` — ссылка показателя «Активности», группы «Сводки и связей» и т. п.
 * Ключ и значение кодируются, квадратные скобки фильтра остаются читаемыми (реестр читает параметры
 * через URLSearchParams — любой вид кодирования скобок равнозначен).
 */
export function sectionLinkHref(link: SectionLink): string {
  const query = Object.entries(link.filters)
    .map(([key, value]) => `filter[${encodeURIComponent(key)}]=${encodeURIComponent(value)}`)
    .join('&');
  const path = ROUTES.section(link.section);
  return query ? `${path}?${query}` : path;
}

/**
 * Возврат из карточки не в её реестр, а в раздел-источник (ТЗ SRV 13.7.2: из карточки, открытой
 * из «Модерации», «Назад» возвращает в «Модерацию»): переход с `state: backLinkState(…)`, карточка
 * читает его `readBackLink(location.state)`. Только относительный путь внутри админки.
 */
export interface BackLink {
  to: string;
  label: string;
}

export interface BackLinkState {
  back: BackLink;
}

export function backLinkState(to: string, label: string): BackLinkState {
  return { back: { to, label } };
}

export function readBackLink(state: unknown): BackLink | null {
  if (typeof state !== 'object' || state === null || !('back' in state)) return null;
  const back = state.back;
  if (typeof back !== 'object' || back === null) return null;
  if (!('to' in back) || !('label' in back)) return null;
  const { to, label } = back;
  if (typeof to !== 'string' || typeof label !== 'string') return null;
  if (!to.startsWith('/') || to.startsWith('//')) return null;
  return { to, label };
}

/** Параметр возврата после входа: `/login?from=/wines?page=2`. */
export const RETURN_TO_PARAM = 'from';

/** Только относительный путь внутри админки (защита от open redirect). */
export function safeReturnPath(value: string | null | undefined): string | null {
  if (!value?.startsWith('/') || value.startsWith('//') || value.startsWith('/\\')) {
    return null;
  }
  if (value === ROUTES.login || value.startsWith(`${ROUTES.login}?`)) return null;
  return value;
}

export function loginPath(returnTo?: string | null): string {
  const safe = safeReturnPath(returnTo);
  if (!safe || safe === ROUTES.root) return ROUTES.login;
  return `${ROUTES.login}?${new URLSearchParams({ [RETURN_TO_PARAM]: safe }).toString()}`;
}
